import { Component, ElementRef, EventEmitter, inject, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { debounceTime, firstValueFrom, Observable, Subject, Subscription } from 'rxjs';
import { ENVIRONMENT } from 'projects/shared/lib/environment/environment';
import { QuantityInputBareChangeResult } from './change-result/quantity-input-bare-change-result.model';
import { ServiceLocator } from 'projects/shared/lib/services/infrastructure/service-locator.service';
import { MobileDetectionService } from 'projects/shared/lib/services/ui/mobile-detection.service';
import { QuantityValidationService } from 'projects/shared/lib/services/infrastructure/quantity-validation/quantity-validation.service';
import { IConfigurationService, IConfigurationServiceToken } from 'projects/shared/lib/services/infrastructure/configuration/configuration-service.interface';

@Component({
  selector: 'app-quantity-input-bare',
  imports: [
    CommonModule,
    FormsModule,
  ],
  templateUrl: './quantity-input-bare.component.html',
  styleUrls: ['./quantity-input-bare.component.scss']
})
export class QuantityInputBareComponent implements OnInit, OnChanges, OnDestroy {
  @Input() public min: number;
  @Input() public max: number;
  @Input() public quantity: number;
  @Input() public disabled: boolean | undefined;
  @Input() public accept: Observable<void> | undefined;
  @Input() public reject: Observable<void> | undefined;
  @Output() public onQuantityChanged: EventEmitter<QuantityInputBareChangeResult>;
  @ViewChild('quantityInput') quantityInput!: ElementRef<HTMLInputElement>;

  private readonly configurationService: IConfigurationService = inject(IConfigurationServiceToken);
  private shouldRemoveProductFromCartUponNegativeQuantity?: boolean | undefined;
  private quantityChangeDebounceTimeInMs: number;
  private quantitySubject: Subject<QuantityInputBareChangeResult>;

  private quantityPreviousValue: number;
  private innerHeight: number;
  public keyboardHeight: number;
  private mobileDetectionService: MobileDetectionService;
  private quantityValidationService: QuantityValidationService;
  private isMobileOrTablet: boolean | undefined;
  private deviceTypeSubscription: Subscription | undefined;

  public cartPreviewQuantityInputBareHasButtons: boolean;

  public constructor() {
    this.min = 1;
    this.max = ENVIRONMENT.checkout.maximumOrderAmount;
    this.quantity = 1;
    this.quantityPreviousValue = this.quantity;
    this.getConfigurationAsync();
    this.quantityChangeDebounceTimeInMs = ENVIRONMENT.quantityChangeDebounceTimeInMs;
    this.mobileDetectionService = ServiceLocator.injector.get(MobileDetectionService);
    this.quantityValidationService = ServiceLocator.injector.get(QuantityValidationService);

    this.onQuantityChanged = new EventEmitter();
    this.quantitySubject = new Subject();
    this.keyboardHeight = 0;
    this.innerHeight = window.innerHeight;

    this.cartPreviewQuantityInputBareHasButtons = ENVIRONMENT.cart.cartPreviewQuantityInputBareHasButtons != undefined ? ENVIRONMENT.cart.cartPreviewQuantityInputBareHasButtons : true;
  }

  public async getConfigurationAsync() {
    this.shouldRemoveProductFromCartUponNegativeQuantity = await firstValueFrom(this.configurationService.shouldRemoveProductFromCartUponNegativeQuantityGet());
    let checkoutConfiguration = await firstValueFrom(this.configurationService.checkoutConfigurationGet());
    if (checkoutConfiguration.maximumOrderAmount != undefined) {
      this.max = checkoutConfiguration.maximumOrderAmount;
    }
  }

  public ngOnInit(): void {
    this.deviceTypeSubscription = this.mobileDetectionService.deviceType$.subscribe((deviceType: string) => {
      this.isMobileOrTablet = deviceType == 'mobile' || deviceType == 'tablet' ? true : false;
    });

    this.quantityPreviousValue = this.quantity;

    this.quantitySubject.pipe(
      debounceTime(this.quantityChangeDebounceTimeInMs)
    ).subscribe(
      (quantity) => {
        this.onQuantityChanged.emit(quantity);
      }
    );

    this.accept?.subscribe(
      () => this.quantityPreviousValue = this.quantity
    );

    this.reject?.subscribe(
      () => this.quantity = this.quantityPreviousValue
    );
  }

  public ngOnDestroy(): void {
    this.deviceTypeSubscription?.unsubscribe();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    for (let propName in changes) {
      let change = changes[propName];
      switch (propName) {
        case 'quantity': {
          this.quantityPreviousValue = change.currentValue;
          break;
        }
      }
    }
  }

  public onFocus() {
    if (this.isMobileOrTablet) {
      let isProductList = this.quantityInput.nativeElement.closest('.product-list-item-grid, .product-list-item-list') != null ? true : false;
      if (isProductList) {
        if (this.innerHeight < window.innerHeight) {
          this.keyboardHeight = window.innerHeight - this.innerHeight;
        }
        if (this.innerHeight > window.innerHeight) {
          this.keyboardHeight = 0;
        }
        this.innerHeight = window.innerHeight;
        setTimeout(() => {
          let inputRect = this.quantityInput.nativeElement.getBoundingClientRect();
          let inputBottom = inputRect.bottom + this.keyboardHeight;
          if (inputBottom > window.innerHeight) {
            let scrollPosition = window.pageYOffset + (inputBottom - window.innerHeight);
            window.scrollTo({ top: scrollPosition, behavior: 'smooth' });
          }
        }, 150);
      }
    }
  }

  public quantityDecrease() {
    this.quantity = this.quantity - this.min;
    this.quantityChanged();
  }

  public quantityIncrease() {
    this.quantity = this.quantity + this.min;
    this.quantityChanged();
  }

  public onEnterPressed() {
    this.quantityInput.nativeElement.focus();
    this.quantityInput.nativeElement.blur();
    this.quantityChanged();
  }

  public quantityChanged() {
    let quantity = this.quantity;
    let minimumOrderAmount = this.min;
    let maximumOrderAmount = this.max;

    let quantityChangeResult = this.quantityValidationService.validateQuantity(quantity, minimumOrderAmount, maximumOrderAmount);

    this.quantitySubject.next(quantityChangeResult);
  }
}
